import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  Input,
  VStack,
  useToast,
  Text,
} from '@chakra-ui/react';
import { postData, getData } from '../../utils/httpClient.js';

export default function CreateRevalidationModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    studentId: '',
    subjectId: '',
    originInstitute: '',
    acceptanceDate: '',
  });
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [showCustomInstitute, setShowCustomInstitute] = useState(false);
  const [customInstitute, setCustomInstitute] = useState('');
  const toast = useToast();

  // Cargar estudiantes y materias al abrir el modal
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  const loadInitialData = async () => {
    setLoadingData(true);
    try {
      const [studentsResponse, subjectsResponse] = await Promise.all([
        getData('/person/student?limit=1000'),
        getData('/subjects?limit=1000'),
      ]);

      console.log('Students response:', studentsResponse);
      console.log('Subjects response:', subjectsResponse);

      // El endpoint de person devuelve 'people', no 'students'
      const studentsData = studentsResponse?.payload?.people || [];
      const subjectsData = subjectsResponse?.payload?.subjects || subjectsResponse?.payload || [];

      setStudents(studentsData);
      setSubjects(subjectsData);

      // Intentar cargar instituciones, pero no fallar si no existe el endpoint
      try {
        const institutionsResponse = await getData('/institutions?country=Uruguay&enabled=true');
        console.log('Institutions response:', institutionsResponse);
        const institutionsData = institutionsResponse?.payload || [];
        setInstitutions(Array.isArray(institutionsData) ? institutionsData : []);
      } catch (institutionError) {
        console.warn('Instituciones endpoint not available yet:', institutionError);
        setInstitutions([]);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast({
        title: 'Error al cargar datos',
        description: 'No se pudieron cargar los datos necesarios',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'originInstitute') {
      if (value === 'Otro Instituto') {
        setShowCustomInstitute(true);
        setFormData(prev => ({ ...prev, [field]: '' }));
      } else {
        setShowCustomInstitute(false);
        setCustomInstitute('');
        setFormData(prev => ({ ...prev, [field]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleCustomInstituteChange = (value) => {
    setCustomInstitute(value);
    setFormData(prev => ({ ...prev, originInstitute: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.studentId || !formData.subjectId || !formData.originInstitute || !formData.acceptanceDate) {
      toast({
        title: 'Campos requeridos',
        description: 'Por favor complete todos los campos obligatorios',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        studentId: parseInt(formData.studentId),
        subjectId: parseInt(formData.subjectId),
        originInstitute: formData.originInstitute,
        acceptanceDate: formData.acceptanceDate,
        createdBy: 1, // TODO: obtener del contexto de usuario
      };

      await postData('/revalidations', payload);
      
      toast({
        title: 'Reválida creada exitosamente',
        description: 'La reválida se ha registrado y se ha actualizado la escolaridad del estudiante',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error creating revalidation:', error);
      toast({
        title: 'Error al crear reválida',
        description: error.response?.data?.message || error.message || 'Ha ocurrido un error inesperado',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      studentId: '',
      subjectId: '',
      originInstitute: '',
      acceptanceDate: '',
    });
    setShowCustomInstitute(false);
    setCustomInstitute('');
    onClose();
  };

  const selectedStudent = students.find(person => person.student?.id === parseInt(formData.studentId));
  const selectedSubject = subjects.find(s => s.id === parseInt(formData.subjectId));

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>Nueva Reválida</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Estudiante</FormLabel>
                <Select
                  placeholder="Seleccione un estudiante"
                  value={formData.studentId}
                  onChange={(e) => handleInputChange('studentId', e.target.value)}
                  isDisabled={loadingData}
                >
                  {students.map((person) => (
                    <option key={person.student?.id} value={person.student?.id}>
                      {person.name} {person.lastName} {person.maternalLastName || ''} - {person.document}
                    </option>
                  ))}
                </Select>
                {selectedStudent && (
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Estudiante: {selectedStudent.name} {selectedStudent.lastName} {selectedStudent.maternalLastName || ''} - Documento: {selectedStudent.document}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Materia</FormLabel>
                <Select
                  placeholder="Seleccione una materia"
                  value={formData.subjectId}
                  onChange={(e) => handleInputChange('subjectId', e.target.value)}
                  isDisabled={loadingData}
                >
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.description} - Ciclo {subject.cycle}
                    </option>
                  ))}
                </Select>
                {selectedSubject && (
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Ciclo: {selectedSubject.cycle} - Carrera: {selectedSubject.study?.description}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Instituto de Origen</FormLabel>
                <Select
                  placeholder="Seleccione el instituto de origen"
                  value={showCustomInstitute ? 'Otro Instituto' : formData.originInstitute}
                  onChange={(e) => handleInputChange('originInstitute', e.target.value)}
                >
                  {Array.isArray(institutions) && institutions.map((institucion) => (
                    <option key={institucion.id} value={institucion.name}>
                      {institucion.name}
                    </option>
                  ))}
                  <option value="Otro Instituto">Otro Instituto</option>
                </Select>
                {showCustomInstitute && (
                  <Input
                    placeholder="Ingrese el nombre del instituto"
                    value={customInstitute}
                    onChange={(e) => handleCustomInstituteChange(e.target.value)}
                    mt={2}
                    maxLength={500}
                  />
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Fecha de Aceptación</FormLabel>
                <Input
                  type="date"
                  value={formData.acceptanceDate}
                  onChange={(e) => handleInputChange('acceptanceDate', e.target.value)}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isDisabled={loadingData}
            >
              Crear Reválida
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
